import type { SeccionDeTerreno } from "@/lib/terreno";

/* ---------------------------------------------------------------------------
   La lógica PURA del selector «Clasificar» de la pestaña de Terreno: qué
   secciones se ofrecen, qué se puede elegir dentro de cada una, cómo se busca
   y cómo se resume lo elegido.

   Vive FUERA del componente por la misma razón que `registroDeTerreno.ts`: una
   opción de más o de menos no rompe nada a la vista, y el servidor rechaza con
   422 lo que el selector no debió ofrecer. Acá cada regla se prueba con datos
   de mano.

   LAS OPCIONES SON LAS QUE EL API ADMITE (`admiteLaClasificacion`): los ítems
   y campos que la propia vista lista, más los cuatro puntos de registro de
   antecedentes generales. El GPS y la portada no son texto que se redacte y
   ni la App de terreno los ofrece.
--------------------------------------------------------------------------- */

export interface OpcionDeDestino {
  /** `null` es la sección completa; si no, el `item_ref` que viaja al API. */
  referencia: string | null;
  nombre: string;
  /** Lo que ES la opción: «Campo», «Punto de registro» o el título de la lista de ítems. */
  tipo: string;
}

/** Con más opciones que esto el selector ofrece un buscador. */
const OPCIONES_SIN_BUSCADOR = 6;

/** Los campos de una sección que no admiten una captura (el API los rechaza). */
const CAMPOS_SIN_CAPTURAS = ["gps", "portada"];

/**
 * El vocabulario CERRADO de puntos de registro de antecedentes generales, el
 * mismo de `VistaDelRegistroDeTerreno::PUNTOS_DE_REGISTRO` y de la App. El API
 * no los incluye en la respuesta (no son datos del informe), así que viajan acá.
 */
const PUNTOS_DE_REGISTRO: { id: string; nombre: string }[] = [
  { id: "punto-de-acceso", nombre: "Punto de acceso al predio" },
  { id: "vista-general", nombre: "Vista general de la propiedad" },
  { id: "deslindes", nombre: "Deslindes y cercos perimetrales" },
  { id: "caminos", nombre: "Caminos y vías de acceso" },
];

export const SECCION_SIN_CLASIFICAR = "sin_clasificar";

/** Todas las secciones menos «sin clasificar», que es de donde sale la captura. */
export function seccionesClasificables(secciones: SeccionDeTerreno[]): SeccionDeTerreno[] {
  return secciones.filter((s) => s.clave !== SECCION_SIN_CLASIFICAR);
}

export function opcionesDeLaSeccion(seccion: SeccionDeTerreno): OpcionDeDestino[] {
  const opciones: OpcionDeDestino[] = [
    { referencia: null, nombre: "Sección completa", tipo: "Sin ítem específico" },
  ];

  for (const campo of seccion.campos) {
    if (CAMPOS_SIN_CAPTURAS.includes(campo.clave)) continue;
    opciones.push({ referencia: `campo:${campo.clave}`, nombre: campo.nombre, tipo: "Campo" });
  }

  if (seccion.clave === "antecedentes_generales") {
    for (const punto of PUNTOS_DE_REGISTRO) {
      opciones.push({
        referencia: `punto:${punto.id}`,
        nombre: punto.nombre,
        tipo: "Punto de registro",
      });
    }
  }

  for (const item of seccion.items) {
    opciones.push({
      referencia: item.referencia,
      nombre: item.nombre,
      tipo: seccion.items_titulo ?? "Ítem",
    });
  }

  return opciones;
}

export function mostrarBuscador(opciones: OpcionDeDestino[]): boolean {
  return opciones.length > OPCIONES_SIN_BUSCADOR;
}

/** Minúsculas y sin tildes: «ubicacion» encuentra «Descripción de la ubicación». */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function filtrarOpciones(opciones: OpcionDeDestino[], busqueda: string): OpcionDeDestino[] {
  const aguja = normalizar(busqueda);
  if (aguja === "") return opciones;

  return opciones.filter((o) => normalizar(`${o.nombre} ${o.tipo}`).includes(aguja));
}

/**
 * «Construcciones · Casa patronal»: lo que se va a guardar, dicho en una línea.
 * Una referencia que ya no está entre las opciones (el ítem se borró mientras el
 * panel estaba abierto) se calla: mejor la sección sola que un nombre inventado.
 */
export function resumenDeLaSeleccion(
  secciones: SeccionDeTerreno[],
  destino: string | null,
  itemRef: string | null,
): string {
  const seccion = destino === null ? undefined : secciones.find((s) => s.clave === destino);
  if (!seccion) return "Elija una sección";
  if (itemRef === null) return seccion.nombre;

  const opcion = opcionesDeLaSeccion(seccion).find((o) => o.referencia === itemRef);

  return opcion ? `${seccion.nombre} · ${opcion.nombre}` : seccion.nombre;
}
